// ── Modal: objek yang terkait sebuah TR (lapis config SAP Customizing) ───────
//
// Dipakai DUA halaman: SAPConfigLayer.jsx dan TransportRequest.jsx (tab TR
// status). Keduanya menampilkan baris TR dari sumber yang sama, jadi menyalin
// modal ini ke masing-masing halaman berarti dua salinan aturan "nol objek
// artinya apa" — dan aturan itulah bagian yang paling mudah menyimpang.
//
// ⚠ KAMUS BAHASA SENGAJA LOKAL. Kedua halaman induk memakai sistem i18n yang
// BERBEDA: SAPConfigLayer memakai t() global dari context/translations.js,
// sedangkan TransportRequest memakai kamus STR lokal di dalam berkasnya.
// Komponen ini tidak bisa memakai keduanya tanpa memaksa salah satu induk
// berubah, dan menerima seluruh label lewat props akan memindahkan masalahnya
// ke pemanggil (dua daftar label yang harus dijaga tetap sama). Kamus sendiri
// yang dikunci ke useLang() membuat komponen ini berdiri sendiri.
// Konsekuensinya: scripts/check-i18n.js TIDAK menghitung kunci di berkas ini.
import { useState, useEffect, useCallback, Fragment } from 'react';
import { useLang } from '../context/LanguageContext';
import { X, Search } from 'lucide-react';

const STR = {
  en: {
    title: 'Objects in', close: 'Close',
    search: 'Filter by object name or type…',
    thObj: 'Object', thType: 'Type', thDescr: 'Dictionary text', thKeys: 'Keys',
    loading: 'Reading the config layer…',
    fail: 'Could not load the objects for this TR.',
    noneMatch: 'No object matches the filter.',
    // Tiga sebab berbeda untuk "tidak ada objek". Lihat catatan di bawah.
    emptyExcluded: 'This TR was excluded from analysis, so its contents were never read. This is not the same as "changes nothing" — nobody has looked.',
    emptyUnreleased: 'This TR has not been released yet. Its object list is only recorded once it is released.',
    emptyWorkbench: 'This TR changes no customizing object. Its contents are most likely workbench objects — ABAP code, user exits, function groups, screens — which this layer does not cover. The TR itself is real and was analysed; it simply holds nothing of the kind recorded here.',
    emptyReal: 'The object count for this TR was never recorded, and no object row is stored either. Nothing here says the TR is empty — only that nobody measured it.',
    keysNone: 'not recorded',
    keysNoneHint: 'Key counts exist only for TABU objects — a view name never matches E071K. This is not zero.',
    ddNone: 'not in the data dictionary',
    count: 'objects', showing: 'showing',
    liveBtn: 'Read all objects from SAP',
    liveBusy: 'Reading from SAP…',
    liveHint: 'Reads E071 live from the DEV system, including workbench objects this layer does not store.',
    liveNone: 'SAP returned no object for this TR either.',
    liveNoPerm: 'Reading live from SAP needs the transport permission.',
    liveFail: 'Could not read from SAP.',
    liveTruncated: 'The list is cut off at the row limit — there are more objects than shown.',
    liveTitle: 'Live from SAP', liveRequest: 'request', liveTask: 'task',
    liveEmptyTask: 'task with no object',
    liveObjs: 'objects', liveStale: 'Read just now, not from the weekly snapshot.',
    liveCached: 'Stored from an earlier read on',
    liveFresh: 'Read from SAP just now.',
    liveStaleWarn: 'This TR was still modifiable when it was read, so objects may have been added since. Re-read to be sure.',
    liveReread: 'Re-read from SAP',
    excluded: 'Excluded', unreleased: 'Not released',
    srcShow: 'Source', srcHide: 'Hide', srcBusy: 'Reading…',
    srcCached: 'stored', srcFresh: 'read from SAP just now',
    srcLines: 'lines',
    srcReread: 'Re-read',
    srcMissing: 'SAP holds no source for this object.',
    srcEmpty: 'The source exists but contains no active statement — comments and blank lines only.',
    srcDenied: 'SAP refused to return this source (S_DEVELOP, or outside the readable namespace).',
    srcFail: 'Could not read the source.',
    srcTrunc: 'Some lines were cut off at their maximum width by SAP.',
    srcClassNote: 'Shown as the whole class pool, including its method includes. SAP does not expose a way to map a single method to its include over RFC.',
    srcFuncNote: 'Read as the whole function group, because SAP will not return a single function-group include on its own. The include belonging to this function module is shown first and marked.',
    srcThisFm: 'this function module',
  },
  id: {
    title: 'Objek dalam', close: 'Tutup',
    search: 'Saring berdasarkan nama atau tipe objek…',
    thObj: 'Objek', thType: 'Tipe', thDescr: 'Deskripsi kamus', thKeys: 'Kunci',
    loading: 'Membaca lapis config…',
    fail: 'Gagal memuat objek untuk TR ini.',
    noneMatch: 'Tidak ada objek yang cocok dengan filter.',
    emptyExcluded: 'TR ini dikecualikan dari analisis, jadi isinya tidak pernah dibaca. Ini BUKAN sama dengan "tidak mengubah apa pun" — belum ada yang melihatnya.',
    emptyUnreleased: 'TR ini belum dirilis. Daftar objeknya baru tercatat setelah dirilis.',
    emptyWorkbench: 'TR ini tidak mengubah objek customizing. Isinya kemungkinan besar objek workbench — kode ABAP, user exit, function group, screen — yang memang tidak dicakup lapis ini. TR-nya nyata dan sudah dianalisis; ia hanya tidak memuat hal yang dicatat di sini.',
    emptyReal: 'Cacah objek TR ini tidak pernah tercatat, dan tidak ada baris objek yang tersimpan. Tidak ada satu pun di sini yang menyatakan TR-nya kosong — hanya bahwa belum ada yang mengukurnya.',
    keysNone: 'tidak tercatat',
    keysNoneHint: 'Cacah kunci hanya ada untuk objek TABU — nama view tidak pernah cocok ke E071K. Ini BUKAN nol.',
    ddNone: 'tidak ada di kamus data',
    count: 'objek', showing: 'tampil',
    liveBtn: 'Baca semua objek dari SAP',
    liveBusy: 'Membaca dari SAP…',
    liveHint: 'Membaca E071 langsung dari sistem DEV, termasuk objek workbench yang tidak disimpan lapis ini.',
    liveNone: 'SAP juga tidak mengembalikan objek untuk TR ini.',
    liveNoPerm: 'Membaca langsung dari SAP butuh izin transport.',
    liveFail: 'Gagal membaca dari SAP.',
    liveTruncated: 'Daftar terpotong di batas baris — objeknya lebih banyak daripada yang tampil.',
    liveTitle: 'Live dari SAP', liveRequest: 'request', liveTask: 'task',
    liveEmptyTask: 'task tanpa objek',
    liveObjs: 'objek', liveStale: 'Dibaca barusan, bukan dari snapshot mingguan.',
    liveCached: 'Tersimpan dari pembacaan sebelumnya pada',
    liveFresh: 'Dibaca dari SAP barusan.',
    liveStaleWarn: 'TR ini masih bisa diubah saat dibaca, jadi objeknya mungkin sudah bertambah sejak itu. Baca ulang untuk memastikan.',
    liveReread: 'Baca ulang dari SAP',
    excluded: 'Dikecualikan', unreleased: 'Belum dirilis',
    srcShow: 'Source', srcHide: 'Tutup', srcBusy: 'Membaca…',
    srcCached: 'tersimpan', srcFresh: 'dibaca dari SAP barusan',
    srcLines: 'baris',
    srcReread: 'Baca ulang',
    srcMissing: 'SAP tidak memiliki source untuk objek ini.',
    srcEmpty: 'Source-nya ada tapi tidak memuat satu pun perintah aktif — hanya komentar dan baris kosong.',
    srcDenied: 'SAP menolak mengembalikan source ini (S_DEVELOP, atau di luar namespace yang boleh dibaca).',
    srcFail: 'Gagal membaca source.',
    srcTrunc: 'Sebagian baris terpotong di lebar maksimum oleh SAP.',
    srcClassNote: 'Ditampilkan sebagai class pool utuh beserta include method-nya. SAP tidak menyediakan cara memetakan satu method ke include-nya lewat RFC.',
    srcFuncNote: 'Dibaca sebagai function group utuh, karena SAP tidak mau mengembalikan satu include function group secara tersendiri. Include milik function module ini ditaruh paling atas dan ditandai.',
    srcThisFm: 'function module ini',
  },
};

const TH = {
  padding: '0.5rem 0.65rem', textAlign: 'left', fontSize: 11, fontWeight: 700,
  color: '#475569', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap',
  textTransform: 'uppercase', letterSpacing: '0.04em', background: '#f8fafc',
  position: 'sticky', top: 0,
};
const TD = {
  padding: '0.42rem 0.65rem', fontSize: 12.5, color: '#1e293b',
  borderBottom: '1px solid #f1f5f9',
};

export default function TrObjectsModal({ api, connectionId, tr, onClose }) {
  const { lang } = useLang();
  const L = STR[lang] || STR.en;
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  const trkorr = tr?.trkorr || '';
  const isExcluded = !!(tr?.excluded_reason);
  const isUnreleased = String(tr?.trstatus || '').toUpperCase() === 'D';

  useEffect(() => {
    if (!trkorr || !connectionId) return;
    let alive = true;
    setLoading(true); setErr(''); setRows(null);
    // ⚠ excluded=all WAJIB. Di level 'object', filter excluded bekerja lewat
    // subquery ke sap_config_tr: dengan 'none' (bawaan halaman induk) sebuah TR
    // yang DIKECUALIKAN akan mengembalikan nol baris — dan nol baris itu akan
    // terbaca sebagai "TR ini tidak punya objek", padahal sebabnya filter, bukan
    // data. Modal ini menampilkan SATU TR yang sudah dipilih pengguna secara
    // sadar, jadi tidak ada alasan menyaringnya lagi di sini.
    const p = new URLSearchParams({
      connectionId: String(connectionId), level: 'object',
      trkorr, excluded: 'all', sort: 'objname', dir: 'asc', limit: '2000',
    });
    api(`/api/sap/customizing/config?${p.toString()}`)
      .then(d => {
        if (!alive) return;
        setRows(d.rows || []);
        setTotal(d.total || 0);
      })
      .catch(e => { if (alive) { setErr(e.message || L.fail); setRows([]); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [api, connectionId, trkorr, L.fail]);

  // ── Pembacaan LIVE dari SAP (opsional, atas permintaan) ────────────────────
  // Endpoint /api/transport/open/objects membaca E071 langsung dan mengembalikan
  // SELURUH objek, termasuk workbench (function module, report, user exit) yang
  // tidak pernah masuk sap_config_object.
  //
  // Sengaja TIDAK otomatis. Setiap pembukaan modal akan berarti satu panggilan
  // RFC ke sistem DEV, termasuk untuk TR yang datanya sudah lengkap di snapshot —
  // beban jaringan yang tidak diminta siapa pun, pada halaman yang dibuka
  // berkali-kali berturut-turut. Pengguna menekan tombolnya saat memang perlu,
  // dan dengan begitu ia tahu sedang menunggu SAP, bukan menunggu halaman.
  //
  // ⚠ IZIN BERBEDA. Endpoint ini dijaga izin 'transport', sedangkan modal ini
  // bisa dibuka dari halaman yang hanya butuh 'sap_customizing'. 403 di sini
  // BUKAN kegagalan koneksi dan tidak boleh berbunyi seperti itu.
  const [live, setLive] = useState(null);
  const [liveErr, setLiveErr] = useState('');
  const [liveLoading, setLiveLoading] = useState(false);

  // Ganti TR → buang hasil live milik TR sebelumnya. Tanpa ini, membuka TR lain
  // sesudah menekan tombol akan menampilkan objek TR yang SALAH di bawah nomor
  // yang benar — kekeliruan yang tidak melaporkan dirinya sama sekali.
  useEffect(() => { setLive(null); setLiveErr(''); setLiveLoading(false); }, [trkorr]);

  const readLive = useCallback(async (fresh = false) => {
    if (!trkorr || !connectionId) return;
    setLiveLoading(true); setLiveErr('');
    try {
      const p = new URLSearchParams({
        connectionId: String(connectionId), trkorr, limit: '2000',
      });
      // fresh=1 memaksa backend melewati cache. Dipakai HANYA oleh tombol baca
      // ulang; pembukaan biasa selalu boleh memakai cache.
      if (fresh) p.set('fresh', '1');
      const d = await api(`/api/transport/open/objects?${p.toString()}`);
      setLive(d || {});
    } catch (e) {
      setLiveErr(/403|denied|ditolak/i.test(e.message) ? L.liveNoPerm : (e.message || L.liveFail));
    }
    setLiveLoading(false);
  }, [api, connectionId, trkorr, L.liveNoPerm, L.liveFail]);

  // Cache dibaca OTOMATIS saat modal dibuka; SAP tidak disentuh. Endpoint yang
  // sama melayani keduanya, dan hanya menembak SAP bila cache-nya kosong — jadi
  // pembukaan pertama tetap butuh tombol, pembukaan berikutnya tidak.
  //
  // Itu seluruh maksud fitur ini: berhenti menekan tombol yang sama berulang
  // kali untuk TR yang isinya sudah pernah dibaca.
  useEffect(() => {
    if (!trkorr || !connectionId) return;
    let alive = true;
    const p = new URLSearchParams({
      connectionId: String(connectionId), trkorr,
    });
    api(`/api/transport/open/objects/cached?${p.toString()}`)
      .then(d => { if (alive && d && d.found) setLive(d); })
      .catch(() => { /* cache kosong bukan kesalahan; tombol tetap tersedia */ });
    return () => { alive = false; };
  }, [api, connectionId, trkorr]);

  // ── Source ABAP per objek (expand inline) ─────────────────────────────────
  // Daftar tipe yang PUNYA source. Sengaja disalin dari resolver di
  // transportOpen.js dan bukan ditanyakan ke backend: tombolnya harus sudah
  // ada atau tidak ada SEBELUM ada panggilan apa pun. Menampilkan tombol lalu
  // menjawab "tipe ini tidak punya source" membuat setiap baris TABU terlihat
  // seperti sesuatu yang gagal.
  //
  // ⚠ Bila daftar di backend berubah, daftar ini harus ikut. Ketidaksamaannya
  // tidak menimbulkan error — hanya tombol yang hilang, atau tombol yang selalu
  // menjawab kosong.
  const SRC_KEYS = new Set([
    'R3TR PROG', 'LIMU REPS', 'LIMU RINC',
    'R3TR CLAS', 'LIMU CPUB', 'LIMU CPRO', 'LIMU CPRI',
    'LIMU CINC', 'LIMU CLSD', 'LIMU METH', 'LIMU CDEF', 'LIMU CIMP',
    'R3TR FUGR', 'LIMU FUGR', 'LIMU FUNC',
  ]);
  const hasSource = (o) =>
    SRC_KEYS.has(`${String(o.pgmid || '').toUpperCase()} ${String(o.object || '').toUpperCase()}`);

  // Dikunci per objek, bukan per baris tabel: objek yang SAMA sering muncul dua
  // kali dalam satu TR (sekali di request, sekali di task-nya — persis yang
  // terlihat pada D01K916727). Memakai kunci per objek membuat keduanya berbagi
  // satu hasil, jadi membuka yang kedua tidak menembak SAP lagi.
  const srcKey = (o) => `${o.pgmid}|${o.object}|${o.name}`;

  const [srcOpen, setSrcOpen] = useState({});
  const [srcData, setSrcData] = useState({});
  const [srcBusy, setSrcBusy] = useState({});
  const [srcErr,  setSrcErr]  = useState({});

  // Ganti TR → buang seluruh source milik TR sebelumnya, dengan alasan yang
  // sama seperti pada `live`: menampilkan kode objek TR lain di bawah nomor
  // yang benar adalah kekeliruan yang tidak melaporkan dirinya.
  useEffect(() => { setSrcOpen({}); setSrcData({}); setSrcBusy({}); setSrcErr({}); }, [trkorr]);

  const loadSource = useCallback(async (o, fresh = false) => {
    const k = srcKey(o);
    setSrcBusy(s => ({ ...s, [k]: true }));
    setSrcErr(s => ({ ...s, [k]: '' }));
    try {
      const p = new URLSearchParams({
        connectionId: String(connectionId),
        pgmid: o.pgmid || '', object: o.object || '', name: o.name || '',
      });
      if (fresh) p.set('fresh', '1');
      const d = await api(`/api/transport/open/objects/source?${p.toString()}`);
      setSrcData(s => ({ ...s, [k]: d || {} }));
    } catch (e) {
      const m = String(e.message || '');
      setSrcErr(s => ({ ...s, [k]: /403|denied|ditolak/i.test(m) ? L.srcDenied : (m || L.srcFail) }));
    }
    setSrcBusy(s => ({ ...s, [k]: false }));
  }, [api, connectionId, L.srcDenied, L.srcFail]);

  const toggleSource = useCallback((o) => {
    const k = srcKey(o);
    const nowOpen = !srcOpen[k];
    setSrcOpen(s => ({ ...s, [k]: nowOpen }));
    // Dibaca hanya pada pembukaan PERTAMA. Menutup lalu membuka lagi memakai
    // hasil yang sudah ada — kalau tidak, menelusuri sebuah TR berarti puluhan
    // panggilan RFC untuk kode yang sudah ada di layar semenit sebelumnya.
    if (nowOpen && !srcData[k] && !srcBusy[k]) loadSource(o, false);
  }, [srcOpen, srcData, srcBusy, loadSource]);

  // Esc menutup. Modal tanpa jalan keluar lewat papan ketik memaksa pengguna
  // mencari tombol X dengan tetikus setiap kali — dan modal ini akan dibuka
  // berkali-kali berturut-turut saat menelusuri daftar TR.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = (rows || []).filter(r => {
    if (!q.trim()) return true;
    const needle = q.trim().toUpperCase();
    return String(r.objname || '').toUpperCase().includes(needle)
        || String(r.objtype || '').toUpperCase().includes(needle)
        || String(r.descr || '').toUpperCase().includes(needle);
  });

  const stop = useCallback((e) => e.stopPropagation(), []);

  // Nol baris punya EMPAT sebab yang menuntun ke tindakan berbeda, dan TIDAK SATU
  // pun di antaranya berarti "TR ini kosong". Menyamakan keempatnya menjadi
  // "tidak ada data" adalah cara paling halus untuk menyesatkan pembacanya.
  //
  //   1. dikecualikan        → isinya tidak pernah dibaca
  //   2. belum dirilis       → daftar objek belum tercatat
  //   3. obj_count = 0       → DIANALISIS dan memang nol objek CUSTOMIZING.
  //                            Ini sebab yang PALING SERING, dan yang paling
  //                            mudah disalahbaca. Lapis ini hanya menyimpan 8
  //                            objtype data customizing (CFG_DATA_OBJECTS di
  //                            sapCustomizing.js); TR berisi kode ABAP, user
  //                            exit, function group, screen — pekerjaan
  //                            workbench — sah dan lengkap di E071, tetapi tidak
  //                            menyumbang satu baris pun ke sap_config_object.
  //   4. obj_count = NULL    → cacahnya tidak pernah diukur
  //
  // Urutannya penting: sebuah TR bisa memenuhi lebih dari satu, dan yang paling
  // menjelaskan harus menang.
  const analysedZero = !isExcluded && !isUnreleased && tr?.obj_count === 0;
  const emptyMsg = isExcluded ? L.emptyExcluded
    : isUnreleased ? L.emptyUnreleased
    : analysedZero ? L.emptyWorkbench
    : L.emptyReal;

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
      <div onClick={stop}
        style={{ background: '#fff', borderRadius: 14, width: 'min(920px, 100%)',
          maxHeight: '85vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(15,23,42,0.25)' }}>

        <div style={{ padding: '0.9rem 1.1rem', borderBottom: '1px solid #e2e8f0',
          display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
              {L.title} <span style={{ fontFamily: 'monospace' }}>{trkorr}</span>
              {isExcluded && (
                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px',
                  borderRadius: 999, background: '#fef3c7', color: '#92400e' }}>{L.excluded}</span>
              )}
              {isUnreleased && (
                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px',
                  borderRadius: 999, background: '#f1f5f9', color: '#475569' }}>{L.unreleased}</span>
              )}
            </div>
            {tr?.descr && (
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{tr.descr}</div>
            )}
          </div>
          <button onClick={onClose} title={L.close}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#64748b', padding: 4, lineHeight: 0 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '0.7rem 1.1rem', borderBottom: '1px solid #f1f5f9',
          display: 'flex', gap: 10, alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={L.search}
              style={{ width: '100%', padding: '0.45rem 0.6rem 0.45rem 2rem', borderRadius: 9,
                border: '1px solid #cbd5e1', fontSize: 12.5 }} />
          </div>
          <div style={{ fontSize: 11.5, color: '#94a3b8', whiteSpace: 'nowrap' }}>
            {total.toLocaleString()} {L.count}
            {q.trim() && <> · {L.showing} {filtered.length.toLocaleString()}</>}
          </div>
        </div>

        <div style={{ overflow: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ padding: '1.4rem', textAlign: 'center', color: '#94a3b8', fontSize: 12.5 }}>
              {L.loading}
            </div>
          )}
          {!loading && err && (
            <div style={{ margin: '1rem', padding: '0.7rem 0.9rem', background: '#fef2f2',
              border: '1px solid #fecaca', borderRadius: 9, fontSize: 12.5, color: '#b91c1c' }}>{err}</div>
          )}
          {!loading && !err && rows !== null && !rows.length && (
            <div style={{ margin: '1rem', padding: '0.9rem', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: 9, fontSize: 12.5, color: '#92400e',
              lineHeight: 1.55 }}>{emptyMsg}</div>
          )}
          {!loading && !err && !!(rows || []).length && (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={TH}>{L.thObj}</th>
                  <th style={TH}>{L.thType}</th>
                  <th style={TH}>{L.thDescr}</th>
                  <th style={{ ...TH, textAlign: 'right' }}>{L.thKeys}</th>
                </tr>
              </thead>
              <tbody>
                {!filtered.length && (
                  <tr><td colSpan={4} style={{ ...TD, textAlign: 'center', color: '#94a3b8',
                    padding: '1.2rem' }}>{L.noneMatch}</td></tr>
                )}
                {filtered.map((r, i) => (
                  <tr key={`${r.objname}|${r.objtype}|${i}`}>
                    <td style={{ ...TD, fontFamily: 'monospace' }}>{r.objname}</td>
                    <td style={TD}>{r.objtype}</td>
                    <td style={{ ...TD, maxWidth: 360 }}>
                      {r.descr
                        ? r.descr
                        : <span style={{ color: '#cbd5e1', fontStyle: 'italic', fontSize: 11.5 }}>
                            {L.ddNone}
                          </span>}
                    </td>
                    {/* key_count NULL ≠ 0. NULL berarti cacahnya TIDAK TERCATAT
                        (68% baris: nama view di E071 tidak pernah cocok dengan
                        tabel fisik di E071K), dan menulis 0 di sana menyatakan
                        "tidak ada baris kunci berubah" — pernyataan yang tidak
                        pernah diukur siapa pun. */}
                    <td style={{ ...TD, textAlign: 'right' }}>
                      {r.key_count == null
                        ? <span style={{ color: '#cbd5e1', fontStyle: 'italic', fontSize: 11.5,
                            cursor: 'help' }} title={L.keysNoneHint}>{L.keysNone}</span>
                        : r.key_count.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {/* Tombol baca-live. Selalu tersedia, bukan hanya saat hasil nol: TR yang
              PUNYA objek customizing bisa juga memuat objek workbench, dan
              menyembunyikan tombolnya di situ akan membuat daftar terlihat
              lengkap padahal separuh. */}
          {!loading && !err && (
            <div style={{ margin: '0.9rem 1.1rem 0', paddingTop: (rows || []).length ? '0.8rem' : 0,
              borderTop: (rows || []).length ? '1px solid #f1f5f9' : 'none' }}>
              {!live && (
                <>
                  <button onClick={() => readLive(false)} disabled={liveLoading || !connectionId}
                    style={{ padding: '0.45rem 0.9rem', borderRadius: 9, fontSize: 12.5,
                      fontWeight: 600, cursor: liveLoading ? 'default' : 'pointer',
                      border: '1px solid #0f766e', background: liveLoading ? '#f1f5f9' : '#f0fdfa',
                      color: '#0f766e', opacity: liveLoading ? 0.7 : 1 }}>
                    {liveLoading ? L.liveBusy : L.liveBtn}
                  </button>
                  <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 6, lineHeight: 1.5 }}>
                    {L.liveHint}
                  </div>
                </>
              )}

              {!!liveErr && (
                <div style={{ padding: '0.6rem 0.8rem', background: '#fef2f2',
                  border: '1px solid #fecaca', borderRadius: 9, fontSize: 12.5,
                  color: '#b91c1c', marginTop: 8 }}>{liveErr}</div>
              )}

              {live && (
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                    <strong style={{ fontSize: 12, color: '#0f766e', textTransform: 'uppercase',
                      letterSpacing: '0.04em' }}>{L.liveTitle}</strong>
                    <span style={{ fontSize: 11.5, color: '#94a3b8' }}>
                      {(live.total_objects || 0).toLocaleString()} {L.liveObjs}
                      {' · '}
                      {live.cached
                        ? `${L.liveCached} ${String(live.read_at || '').slice(0, 16).replace('T', ' ')}`
                        : L.liveFresh}
                    </span>
                    {/* Tombol baca ulang hanya muncul untuk hasil TERSIMPAN.
                        Untuk hasil yang baru saja dibaca, membacanya lagi tidak
                        akan memberi apa pun selain menunggu. */}
                    {live.cached && (
                      <button onClick={() => readLive(true)} disabled={liveLoading}
                        style={{ marginLeft: 'auto', padding: '0.25rem 0.6rem', borderRadius: 8,
                          fontSize: 11.5, fontWeight: 600, cursor: liveLoading ? 'default' : 'pointer',
                          border: '1px solid #cbd5e1', background: '#fff', color: '#475569' }}>
                        {liveLoading ? L.liveBusy : L.liveReread}
                      </button>
                    )}
                  </div>

                  {/* Peringatan usang HANYA untuk TR yang masih bisa diubah saat
                      dibaca. TR yang sudah rilis isinya beku permanen — memberinya
                      peringatan yang sama akan melatih orang mengabaikannya di
                      tempat yang justru penting. */}
                  {live.stale && (
                    <div style={{ padding: '0.5rem 0.7rem', background: '#fffbeb',
                      border: '1px solid #fde68a', borderRadius: 8, fontSize: 12,
                      color: '#92400e', marginBottom: 8 }}>{L.liveStaleWarn}</div>
                  )}

                  {live.truncated && (
                    <div style={{ padding: '0.5rem 0.7rem', background: '#fffbeb',
                      border: '1px solid #fde68a', borderRadius: 8, fontSize: 12,
                      color: '#92400e', marginBottom: 8 }}>{L.liveTruncated}</div>
                  )}

                  {!(live.tasks || []).length && !(live.empty_tasks || []).length && (
                    <div style={{ padding: '0.7rem 0.9rem', background: '#f8fafc',
                      border: '1px solid #e2e8f0', borderRadius: 9, fontSize: 12.5,
                      color: '#64748b' }}>{L.liveNone}</div>
                  )}

                  {/* Dikelompokkan per task, seperti pohon SE01 — bentuk yang sama
                      dengan tab antrean, supaya orang yang sudah terbiasa di sana
                      tidak perlu belajar tata letak kedua. */}
                  {(live.tasks || []).map(tk => (
                    <div key={tk.trkorr} style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, color: '#334155', marginBottom: 4 }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{tk.trkorr}</span>
                        <span style={{ color: '#94a3b8', marginLeft: 6 }}>
                          {tk.is_request ? L.liveRequest : L.liveTask}
                          {tk.owner ? ` · ${tk.owner}` : ''}
                          {tk.status ? ` · ${tk.status}` : ''}
                          {` · ${(tk.objects || []).length} ${L.liveObjs}`}
                        </span>
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                          {(tk.objects || []).map((o, i) => {
                            const k = srcKey(o);
                            const can = hasSource(o);
                            const open = !!srcOpen[k];
                            const d = srcData[k];
                            return (
                              <Fragment key={`${tk.trkorr}|${o.pos}|${i}`}>
                                <tr>
                                  <td style={{ ...TD, width: 170, color: '#475569' }}>{o.type_label}</td>
                                  <td style={{ ...TD, fontFamily: 'monospace' }}>{o.name}</td>
                                  <td style={{ ...TD, width: 90, color: '#94a3b8', fontSize: 11.5 }}>
                                    {o.pgmid} {o.object}
                                  </td>
                                  {/* Tombol DISEMBUNYIKAN untuk tipe tanpa source, bukan
                                      dinonaktifkan: tombol mati tetap mengundang klik dan
                                      tetap harus dijelaskan. Sel-nya tetap ada supaya
                                      lebar kolom tidak berubah antar baris. */}
                                  <td style={{ ...TD, width: 76, textAlign: 'right' }}>
                                    {can && (
                                      <button onClick={() => toggleSource(o)} disabled={!!srcBusy[k]}
                                        style={{ padding: '0.15rem 0.5rem', borderRadius: 7,
                                          fontSize: 11, fontWeight: 600,
                                          cursor: srcBusy[k] ? 'default' : 'pointer',
                                          border: '1px solid ' + (open ? '#cbd5e1' : '#0f766e'),
                                          background: open ? '#f8fafc' : '#f0fdfa',
                                          color: open ? '#475569' : '#0f766e' }}>
                                        {srcBusy[k] ? L.srcBusy : open ? L.srcHide : L.srcShow}
                                      </button>
                                    )}
                                  </td>
                                </tr>

                                {open && (
                                  <tr>
                                    <td colSpan={4} style={{ padding: '0 0 0.6rem', borderBottom: '1px solid #f1f5f9' }}>
                                      {!!srcErr[k] && (
                                        <div style={{ padding: '0.5rem 0.7rem', background: '#fef2f2',
                                          border: '1px solid #fecaca', borderRadius: 8, fontSize: 12,
                                          color: '#b91c1c' }}>{srcErr[k]}</div>
                                      )}

                                      {!srcErr[k] && srcBusy[k] && (
                                        <div style={{ fontSize: 12, color: '#94a3b8', padding: '0.4rem 0.2rem' }}>
                                          {L.srcBusy}
                                        </div>
                                      )}

                                      {!srcErr[k] && !srcBusy[k] && d && (
                                        <div>
                                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8,
                                            fontSize: 11.5, color: '#94a3b8', marginBottom: 5, flexWrap: 'wrap' }}>
                                            <span style={{ fontFamily: 'monospace', color: '#0f766e', fontWeight: 700 }}>
                                              {d.program}
                                            </span>
                                            {!!d.total_lines && <span>{d.total_lines.toLocaleString()} {L.srcLines}</span>}
                                            <span>· {d.cached ? L.srcCached : L.srcFresh}</span>
                                            <button onClick={() => loadSource(o, true)}
                                              style={{ marginLeft: 'auto', padding: '0.15rem 0.5rem',
                                                borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                                border: '1px solid #cbd5e1', background: '#fff', color: '#475569' }}>
                                              {L.srcReread}
                                            </button>
                                          </div>

                                          {/* Catatan pemetaan. Untuk kelas dan FM, yang dibaca
                                              BUKAN persis objek yang tertulis di TR — dan itu
                                              harus dikatakan, bukan dibiarkan tampak seolah
                                              baris ini adalah isi objeknya sendiri. */}
                                          {d.kind === 'class' && (
                                            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 5,
                                              lineHeight: 1.5 }}>{L.srcClassNote}</div>
                                          )}
                                          {d.kind === 'func' && (
                                            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 5,
                                              lineHeight: 1.5 }}>{L.srcFuncNote}{d.note_object ? ` (${d.note_object})` : ''}</div>
                                          )}

                                          {d.state === 'missing' && (
                                            <div style={{ padding: '0.5rem 0.7rem', background: '#f8fafc',
                                              border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12,
                                              color: '#64748b' }}>{L.srcMissing}{d.note ? ` (${d.note})` : ''}</div>
                                          )}
                                          {d.state === 'empty' && (
                                            <div style={{ padding: '0.5rem 0.7rem', background: '#fffbeb',
                                              border: '1px solid #fde68a', borderRadius: 8, fontSize: 12,
                                              color: '#92400e', marginBottom: 6 }}>{L.srcEmpty}</div>
                                          )}
                                          {!!d.trunc && (
                                            <div style={{ padding: '0.4rem 0.7rem', background: '#fffbeb',
                                              border: '1px solid #fde68a', borderRadius: 8, fontSize: 11.5,
                                              color: '#92400e', marginBottom: 6 }}>{L.srcTrunc}</div>
                                          )}

                                          {/* Satu blok per progname. Nomor baris SAP ikut
                                              ditampilkan dan TIDAK dihitung ulang: nomor itu
                                              yang dipakai orang saat membaca kode yang sama
                                              di SE38/SE24. */}
                                          {(d.groups || []).map(g => {
                                            const isHit = !!d.highlight && g.progname === d.highlight;
                                            return (
                                            <div key={g.progname} style={{ marginBottom: 8 }}>
                                              {((d.groups || []).length > 1 || isHit) && (
                                                <div style={{ fontFamily: 'monospace', fontSize: 11,
                                                  color: isHit ? '#0f766e' : '#64748b',
                                                  fontWeight: isHit ? 700 : 400, margin: '0 0 3px' }}>
                                                  {g.progname}
                                                  <span style={{ color: '#cbd5e1', fontWeight: 400 }}>
                                                    {` · ${(g.lines || []).length} ${L.srcLines}`}
                                                  </span>
                                                  {/* Penanda dibutuhkan karena satu function group
                                                      bisa memuat include milik FM LAIN. Tanpa ini,
                                                      blok pertama akan terbaca sebagai "isi objek
                                                      yang saya klik" padahal belum tentu. */}
                                                  {isHit && (
                                                    <span style={{ marginLeft: 6, padding: '0.05rem 0.35rem',
                                                      borderRadius: 5, background: '#f0fdfa',
                                                      border: '1px solid #99f6e4', color: '#0f766e',
                                                      fontSize: 10, fontWeight: 700 }}>
                                                      {L.srcThisFm}
                                                    </span>
                                                  )}
                                                </div>
                                              )}
                                              <pre style={{ margin: 0, padding: '0.55rem 0.7rem',
                                                background: '#0f172a', color: '#e2e8f0', borderRadius: 8,
                                                fontSize: 11.5, lineHeight: 1.55, overflowX: 'auto',
                                                maxHeight: 420, overflowY: 'auto',
                                                border: isHit ? '1px solid #0f766e' : '1px solid transparent',
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                                                {(g.lines || []).map(ln =>
                                                  String(ln.lineno).padStart(5, ' ') + '  ' + (ln.content || '')
                                                ).join('\n')}
                                              </pre>
                                            </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}

                  {/* Task tanpa objek TETAP dilaporkan — task kosong itu informasi
                      (sering jadi sebab TR tidak bisa di-release), bukan
                      ketiadaan data. */}
                  {(live.empty_tasks || []).map(tk => (
                    <div key={tk.trkorr} style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>
                      <span style={{ fontFamily: 'monospace' }}>{tk.trkorr}</span>
                      {' · '}{L.liveEmptyTask}{tk.owner ? ` · ${tk.owner}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
